import React, { Component, ErrorInfo, ReactNode } from 'react';
import posthog from 'posthog-js';
import ErrorFallback from './ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log to console in development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Record the crash in PostHog so it can be correlated with the session/funnel that
    // led up to it (Sentry has the stack trace; this gives the behavioural context).
    // Production only — matches the provider gate in index.tsx, so posthog is initialised.
    if (process.env.REACT_APP_ANALYTICS_ENV === 'production') {
      posthog.capture('app_error_boundary_caught', {
        error_message: error.message,
        component_stack: errorInfo.componentStack,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return <ErrorFallback />;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
